import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider mb-8 hover:text-[#FF6B6B] transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Back to Home
        </Link>

        <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[6px_6px_0px_0px_#121212] rounded-2xl p-6 md:p-10">
          <h1
            className="text-3xl md:text-4xl font-black uppercase tracking-tighter mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Privacy Policy
          </h1>
          <p className="text-xs font-bold uppercase tracking-wider mb-8" style={{ color: '#888' }}>
            Last updated: April 12, 2026
          </p>

          <div className="space-y-6 text-sm font-medium leading-relaxed" style={{ color: '#444' }}>
            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                1. Information We Collect
              </h2>
              <p className="mb-2">We collect the following types of information when you use The SurfaceArea Project:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Account Information:</strong> Name, email address, and profile picture provided through Google OAuth sign-in.</li>
                <li><strong>Onboarding Information:</strong> City, T-shirt size, YC acceptance verification screenshot, and optional contact details (Twitter/X handle, LinkedIn, phone number).</li>
                <li><strong>Marketplace Activity:</strong> Asking prices, bid history, and transaction records.</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, and general interaction patterns to improve the service.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                2. How We Use Your Information
              </h2>
              <ul className="list-disc pl-5 space-y-1">
                <li>To create and manage your account on the Platform.</li>
                <li>To verify your eligibility as a Y-Combinator Startup School India attendee.</li>
                <li>To display your asks in the marketplace (name, city, and asking price are visible to other users).</li>
                <li>To facilitate matches between Wearers and Startups.</li>
                <li>To send transactional email notifications about ask acceptances, approvals, and rejections.</li>
                <li>To improve and maintain the Platform.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                3. Information Sharing
              </h2>
              <p className="mb-2">We share your information in the following circumstances:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>With Startups:</strong> When a startup accepts your ask, they may receive your name, contact details, and city for fulfillment purposes.</li>
                <li><strong>Admin Review:</strong> Platform administrators review onboarding submissions for verification.</li>
                <li><strong>Public Marketplace:</strong> Your name, city, and asking price are visible on the public marketplace when you have an active ask.</li>
              </ul>
              <p className="mt-2">We do not sell your personal information to third parties.</p>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                4. Data Storage & Security
              </h2>
              <p>
                Your data is stored securely using MongoDB Atlas with industry-standard encryption. Verification screenshots are stored temporarily and deleted after admin review (approval or rejection). We use HTTPS for all data transmission and implement appropriate security measures to protect your information.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                5. Cookies & Authentication
              </h2>
              <p>
                We use session cookies to maintain your authentication state after signing in with Google OAuth. These cookies are essential for the Platform to function and cannot be disabled while using the service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                6. Your Rights
              </h2>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Access:</strong> You can view your profile information and marketplace activity at any time.</li>
                <li><strong>Correction:</strong> You can update your asking price and profile information.</li>
                <li><strong>Deletion:</strong> You may request deletion of your account and associated data by contacting us.</li>
                <li><strong>Portability:</strong> You may request a copy of your data in a standard format.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                7. Email Communications
              </h2>
              <p>
                We send transactional emails related to your marketplace activity (e.g., bid acceptance notifications, approval/rejection updates). These are essential service communications. We do not send marketing emails.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                8. Third-Party Services
              </h2>
              <p>We use the following third-party services:</p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li><strong>Google OAuth:</strong> For user authentication.</li>
                <li><strong>MongoDB Atlas:</strong> For data storage.</li>
                <li><strong>Gmail SMTP:</strong> For sending transactional emails.</li>
              </ul>
              <p className="mt-2">Each of these services has their own privacy policies that govern their handling of your data.</p>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                9. Children's Privacy
              </h2>
              <p>
                The Platform is not intended for use by anyone under the age of 18. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                10. Changes to This Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify users of significant changes through the Platform. Continued use of the Platform after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-black uppercase tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#121212' }}>
                11. Contact Us
              </h2>
              <p>
                If you have questions about this Privacy Policy or wish to exercise your data rights, please contact us at{' '}
                <a href="mailto:support@surfacearea.shop" className="font-bold underline hover:text-[#FF6B6B] transition-colors">
                  support@surfacearea.shop
                </a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
